import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import { films } from "@/mocks/films";
import { useSeo } from "@/hooks/useSeo";
import FilterBar from "./components/FilterBar";

const PAGE_SIZE = 20;
const CATEGORY_KEYS = ["电影", "电视剧", "国产剧", "海外剧"];

type SortKey = "latest" | "rating" | "popularity";

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新上映", icon: "ri-calendar-line" },
  { key: "rating", label: "评分最高", icon: "ri-star-line" },
  { key: "popularity", label: "热度最高", icon: "ri-fire-line" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get("type") ?? "";

  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    initialType && CATEGORY_KEYS.includes(initialType) ? [initialType] : []
  );
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [minRating, setMinRating] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  useSeo({
    title: "久久爱-电影分类与筛选，多维条件快速找片",
    description:
      "久久爱影视分类页汇聚电影、电视剧、国产剧、海外剧等多元作品，支持类型、地区、年份、评分多条件筛选与最新、评分、热度排序，帮你快速找到想看的影视剧。",
    keywords: "久久爱,影视分类,电影筛选,电视剧推荐,类型筛选,影视作品列表",
  });

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    films.forEach((f) => {
      set.add(f.category);
      f.genres.forEach((g) => set.add(g));
    });
    return Array.from(set);
  }, []);

  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    films.forEach((f) => set.add(f.region));
    return Array.from(set);
  }, []);

  const yearOptions = useMemo(() => {
    const set = new Set<number>();
    films.forEach((f) => set.add(f.year));
    return Array.from(set).sort((a, b) => b - a);
  }, []);

  const toggle = <T,>(list: T[], value: T, setter: (v: T[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = films.filter((film) => {
      const typeMatch =
        selectedTypes.length === 0 ||
        selectedTypes.includes(film.category) ||
        film.genres.some((g) => selectedTypes.includes(g));
      const regionMatch =
        selectedRegions.length === 0 || selectedRegions.includes(film.region);
      const yearMatch = selectedYears.length === 0 || selectedYears.includes(film.year);
      const ratingMatch = film.rating >= minRating;
      return typeMatch && regionMatch && yearMatch && ratingMatch;
    });

    result.sort((a, b) => {
      if (sortKey === "rating") return b.rating - a.rating;
      if (sortKey === "popularity") return b.popularity - a.popularity;
      return b.releaseDate.localeCompare(a.releaseDate);
    });

    return result;
  }, [selectedTypes, selectedRegions, selectedYears, minRating, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  // 同步 URL 中的分类参数（首次进入或外部跳转）
  useEffect(() => {
    const urlType = searchParams.get("type") ?? "";
    if (urlType && CATEGORY_KEYS.includes(urlType) && !selectedTypes.includes(urlType)) {
      setSelectedTypes([urlType]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const resetFilters = () => {
    setSelectedTypes([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setMinRating(0);
    setPage(1);
    setSearchParams({});
  };

  const activeDescription =
    selectedTypes.length > 0
      ? `正在浏览「${selectedTypes.join("、")}」相关作品`
      : "从全部影视作品中按条件筛选，找到你想看的那一部";

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: "影视分类" }]} />
            <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-4xl font-heading">
              影视分类与筛选
            </h1>
            <p className="mt-2 text-sm text-foreground-600 md:text-base">
              {activeDescription}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
          <FilterBar
            typeOptions={typeOptions}
            regionOptions={regionOptions}
            yearOptions={yearOptions}
            selectedTypes={selectedTypes}
            selectedRegions={selectedRegions}
            selectedYears={selectedYears}
            minRating={minRating}
            onToggleType={(v) => toggle(selectedTypes, v, setSelectedTypes)}
            onToggleRegion={(v) => toggle(selectedRegions, v, setSelectedRegions)}
            onToggleYear={(v) => toggle(selectedYears, v, setSelectedYears)}
            onRatingChange={(v) => {
              setMinRating(v);
              setPage(1);
            }}
            onReset={resetFilters}
          />

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-bold text-primary-700">{filtered.length}</span>{" "}
              部作品
            </p>
            <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setSortKey(opt.key);
                    setPage(1);
                  }}
                  className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors md:text-sm ${
                    sortKey === opt.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  <i className={`${opt.icon} text-base`}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length === 0 ? (
            <div className="mt-8 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h3 className="text-base font-semibold text-foreground-800">
                未找到符合条件的影视作品
              </h3>
              <p className="text-sm text-foreground-500">
                试试减少筛选条件，或者重置后再看看
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-2 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line text-lg"></i>
                重置筛选条件
              </button>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {pageItems.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-sm transition-colors ${
                  page === 1
                    ? "cursor-not-allowed border-background-200 text-foreground-400"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className="ri-arrow-left-s-line text-base"></i>
                上一页
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setPage(num)}
                  className={`h-9 w-9 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                    num === page
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {num}
                </button>
              ))}

              <button
                type="button"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-sm transition-colors ${
                  page === totalPages
                    ? "cursor-not-allowed border-background-200 text-foreground-400"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                下一页
                <i className="ri-arrow-right-s-line text-base"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}